import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, FilePdf, PaperPlaneTilt, Notebook, ArrowsClockwise, Camera, Files, SealCheck } from "@phosphor-icons/react";
import { api, apiDelete, apiError, apiErrorAsync, downloadFile } from "@/lib/api";
import ProjectBadge from "@/components/ProjectBadge";
import ProjectField, { assertProject, defaultProject } from "@/components/ProjectField";
import { useConfirmDelete } from "@/components/ConfirmDeleteDialog";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DateField } from "@/components/DateField";
import SectionHeader from "@/components/SectionHeader";
import PhotoUploader from "@/components/PhotoUploader";
import SignaturePad from "@/components/SignaturePad";

const SHIFT_LABEL = { dia: "Día", noche: "Noche" };
const EQ_LABEL = {
  anclador: "Anclador", jumbo: "Jumbo", voladuras: "Voladuras",
  scoop: "Scoop tram", camion: "Camión bajo perfil", servicios: "Servicios",
  telehandler: "Tele handler", retro: "Retroexcavadora", motoniv: "Motoniveladora",
};

/** Lazy-loads the bitacora's expanded response (which now returns `kpis`) and
 * shows a compact strip per equipment: hours · delays · availability + key rate. */
function KpiStrip({ logId, testid }) {
  const [kpis, setKpis] = useState(null);
  useEffect(() => {
    api.get(`/bitacora/logs/${logId}`).then((r) => setKpis(r.data.kpis || {})).catch(() => setKpis({}));
  }, [logId]);
  if (kpis === null) return null;
  const entries = Object.entries(kpis);
  if (entries.length === 0) return null;
  return (
    <div className="border-t border-slate-100 pt-3" data-testid={testid}>
      <p className="overline-label mb-2">KPIs de eficiencia por equipo</p>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {entries.map(([eq, k]) => {
          const rate =
            k.m_por_hora != null ? `${k.m_por_hora} m/h` :
            k.ton_por_hora != null ? `${k.ton_por_hora} t/h` :
            k.pernos_por_hora != null ? `${k.pernos_por_hora} pernos/h` :
            k.factor_carga_kg_por_m != null ? `${k.factor_carga_kg_por_m} kg/m` :
            k.horas_maquina != null ? `${k.horas_maquina} h-máq` :
            null;
          const availColor = k.availability_pct >= 85 ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                            : k.availability_pct >= 70 ? "text-amber-700 bg-amber-50 border-amber-200"
                            : "text-red-700 bg-red-50 border-red-200";
          return (
            <div key={eq} className="bg-slate-50 border border-slate-200 rounded p-2 text-xs" data-testid={`bit-kpi-${logId}-${eq}`}>
              <p className="font-heading font-bold text-slate-800 mb-1">{EQ_LABEL[eq] || eq}</p>
              <div className="flex flex-wrap gap-1 text-[10px]">
                <span className="px-1.5 py-0.5 rounded bg-white border border-slate-200"><b>{k.entries}</b> reg</span>
                <span className="px-1.5 py-0.5 rounded bg-white border border-slate-200">⏱ {k.hours_total} h</span>
                {k.delay_hours > 0 && <span className="px-1.5 py-0.5 rounded bg-red-50 border border-red-200 text-red-700">⛔ {k.delay_hours} h</span>}
                <span className={`px-1.5 py-0.5 rounded border font-bold ${availColor}`}>Disp. {k.availability_pct}%</span>
                {rate && <span className="px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-900 font-bold">{rate}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const emptyForm = () => ({
  date: new Date().toISOString().slice(0, 10),
  project_id: defaultProject(),
  shift: "dia", area: "", supervisor_id: "", supervisor_name: "",
  activities_done: "", plan_next: "", resources_needed: [],
  linked_operations: [], materials_used: [], photos: [], status: "open",
  // Iter112: firmas capturadas desde la tablet (dataURL PNG) + nombres visibles.
  signature_conformity: "", signature_conformity_name: "",
  signature_responsible: "", signature_responsible_name: "",
  signature_silverco: "", signature_silverco_name: "",
});

// Iter114: toolbar minimalista de formato para el textarea — permite agregar
// líneas numeradas, con viñeta o con flecha sin salir del teclado virtual.
function FormatToolbar({ onInsert, testid }) {
  const opts = [
    { k: "num", label: "1.", tip: "Numerar" },
    { k: "bul", label: "•",  tip: "Viñeta" },
    { k: "arr", label: "→", tip: "Flecha" },
    { k: "dash", label: "—", tip: "Guión" },
  ];
  return (
    <div className="flex items-center gap-1" data-testid={testid}>
      {opts.map((o) => (
        <button
          key={o.k}
          type="button"
          title={o.tip}
          onClick={() => onInsert(o.k)}
          className="h-6 min-w-[24px] px-1.5 rounded border border-slate-200 bg-white hover:bg-slate-100 text-[11px] font-bold text-slate-700"
          data-testid={`${testid}-${o.k}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function LogForm({ initial, employees, onSaved, onCancel }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [ops, setOps] = useState([]);          // full list for date+shift
  const [selected, setSelected] = useState(new Set(initial.linked_operations || []));
  const [inventoryItems, setInventoryItems] = useState([]);
  const [materials, setMaterials] = useState(initial.materials_used || []);

  // Load active inventory items once so the supervisor can pick from a dropdown
  useEffect(() => {
    api.get("/inventory/items?active=true").then((r) => setInventoryItems(r.data || [])).catch(() => setInventoryItems([]));
  }, []);

  /**
   * Every time date or shift change we auto-pull the operations of that
   * date+shift and, if we're editing, keep the already-selected ids ticked.
   */
  const pullOps = useCallback(async () => {
    if (!form.date || !form.shift) { setOps([]); return; }
    try {
      const r = await api.get(`/bitacora/pull-operations?date=${form.date}&shift=${form.shift}`);
      setOps(r.data);
      // On first load: auto-select ALL (user can uncheck manually).
      setSelected((prev) => {
        if (prev.size > 0) return prev;
        return new Set(r.data.map((o) => o.id));
      });
    } catch (_) { setOps([]); }
  }, [form.date, form.shift]);

  useEffect(() => { pullOps(); }, [pullOps]);

  // El listado excluye `photos` (pesado); al editar se cargan desde el detalle.
  useEffect(() => {
    if (!initial.id || initial.photos !== undefined) return;
    api.get(`/bitacora/logs/${initial.id}`).then((r) => {
      setForm((f) => ({ ...f, photos: r.data.photos || [] }));
    }).catch(() => {});
  }, [initial.id, initial.photos]);

  const toggleOp = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const setResources = (raw) => setForm({ ...form, resources_needed: raw.split("\n").filter(Boolean) });

  // Iter114: helper para insertar prefijo (numerado, viñeta, flecha) al final
  // del texto para que los supervisores puedan estructurar listas rápidamente
  // desde tablet (donde el Enter a veces no dispara salto en teclado virtual).
  const appendLine = (field, prefix) => {
    const cur = (form[field] || "").replace(/\s+$/, "");
    let next;
    if (prefix === "num") {
      const lines = cur.split("\n");
      const nums = lines.map((l) => (l.match(/^\s*(\d+)\./) || [])[1]).filter(Boolean).map(Number);
      const n = (nums.length ? Math.max(...nums) : 0) + 1;
      next = cur ? `${cur}\n${n}. ` : `${n}. `;
    } else {
      const sym = prefix === "bul" ? "• " : prefix === "arr" ? "→ " : "— ";
      next = cur ? `${cur}\n${sym}` : sym;
    }
    setForm({ ...form, [field]: next });
  };

  // Iter115: Enter inteligente — si la línea actual empieza con "N." o con
  // un signo de lista (• → —), al presionar Enter inyecta la continuación
  // sin que el supervisor tenga que tocar la toolbar. Enter en línea vacía
  // rompe la lista (comportamiento clásico de editores como Word/Notion).
  const smartEnter = (field) => (e) => {
    if (e.key !== "Enter" || e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
    const ta = e.target;
    const val = ta.value;
    const caret = ta.selectionStart ?? val.length;
    // Sólo aplicamos smart-enter cuando el caret está al final del texto —
    // así no rompemos correcciones en medio del párrafo.
    if (caret !== val.length) return;
    const lineStart = val.lastIndexOf("\n", caret - 1) + 1;
    const currentLine = val.slice(lineStart, caret);
    const numMatch = currentLine.match(/^(\s*)(\d+)\.\s(.*)$/);
    const bulMatch = currentLine.match(/^(\s*)([•→—])\s(.*)$/);
    if (!numMatch && !bulMatch) return;
    // Línea de lista vacía → romper la lista.
    const content = (numMatch ? numMatch[3] : bulMatch[3]).trim();
    if (!content) {
      e.preventDefault();
      const next = val.slice(0, lineStart) + "\n";
      setForm({ ...form, [field]: next });
      requestAnimationFrame(() => { ta.selectionStart = ta.selectionEnd = next.length; });
      return;
    }
    // Línea con contenido → agregar siguiente ítem.
    e.preventDefault();
    let prefix;
    if (numMatch) {
      prefix = `${numMatch[1]}${parseInt(numMatch[2], 10) + 1}. `;
    } else {
      prefix = `${bulMatch[1]}${bulMatch[2]} `;
    }
    const next = val + "\n" + prefix;
    setForm({ ...form, [field]: next });
    requestAnimationFrame(() => { ta.selectionStart = ta.selectionEnd = next.length; });
  };

  const save = async (close = true) => {
    setBusy(true);
    try {
      const cleanMats = materials.filter((m) => m.item_id && parseFloat(m.qty) > 0)
        .map((m) => ({ item_id: m.item_id, qty: parseFloat(m.qty),
                        operation_id: m.operation_id || "",
                        notes: m.notes || "" }));
      if (!assertProject(form.project_id)) return;
      const payload = { ...form, linked_operations: Array.from(selected),
                        materials_used: cleanMats,
                        photos: form.photos === undefined ? undefined : (form.photos || []),
                        status: close ? "closed" : "open" };
      const r = initial.id
        ? await api.put(`/bitacora/logs/${initial.id}`, payload)
        : await api.post("/bitacora/logs", payload);
      if (r.data?._materials_consumed_now) {
        toast.success(`Bitácora cerrada · ${cleanMats.length} salidas de inventario generadas`);
      } else {
        toast.success(close ? "Bitácora cerrada" : "Bitácora guardada");
      }
      onSaved(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const addMat = () => setMaterials((m) => [...m, { item_id: "", qty: 0, operation_id: "", notes: "" }]);
  const updMat = (idx, k, v) => setMaterials((m) => m.map((x, i) => i === idx ? { ...x, [k]: v } : x));
  const delMat = (idx) => setMaterials((m) => m.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <ProjectField value={form.project_id} onChange={(v) => setForm({ ...form, project_id: v })} testid="bit-form-project" />
        <div><Label className="overline-label">Fecha</Label>
          <DateField testid="bit-form-date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} /></div>
        <div><Label className="overline-label">Turno</Label>
          <Select value={form.shift} onValueChange={(v) => setForm({ ...form, shift: v })}>
            <SelectTrigger data-testid="bit-form-shift"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="dia">Día</SelectItem>
              <SelectItem value="noche">Noche</SelectItem>
            </SelectContent>
          </Select></div>
        <div><Label className="overline-label">Zona / nivel</Label>
          <Input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} data-testid="bit-form-area" /></div>
        <div><Label className="overline-label">Supervisor</Label>
          <Select value={form.supervisor_id || "__na__"} onValueChange={(v) => {
            const emp = employees.find((x) => x.id === v);
            setForm({ ...form, supervisor_id: v === "__na__" ? "" : v, supervisor_name: emp?.name || "" });
          }}>
            <SelectTrigger data-testid="bit-form-supervisor"><SelectValue placeholder="Elegir supervisor…" /></SelectTrigger>
            <SelectContent className="bg-white max-h-72 overflow-y-auto">
              <SelectItem value="__na__">Sin asignar</SelectItem>
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.position}</SelectItem>)}
            </SelectContent>
          </Select></div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <Label className="overline-label">Actividades realizadas</Label>
          <FormatToolbar onInsert={(p) => appendLine("activities_done", p)} testid="bit-fmt-activities" />
        </div>
        <Textarea rows={4} value={form.activities_done} onKeyDown={smartEnter("activities_done")} onChange={(e) => setForm({ ...form, activities_done: e.target.value })} data-testid="bit-form-activities" placeholder="Resumen de lo hecho en el turno… Enter en lista numerada/viñeta continúa la secuencia; Enter en línea vacía la termina." />
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <Label className="overline-label">Plan para el próximo turno</Label>
          <FormatToolbar onInsert={(p) => appendLine("plan_next", p)} testid="bit-fmt-plan" />
        </div>
        <Textarea rows={3} value={form.plan_next} onKeyDown={smartEnter("plan_next")} onChange={(e) => setForm({ ...form, plan_next: e.target.value })} data-testid="bit-form-plan" placeholder="Qué queda pendiente / objetivos…" />
      </div>
      <div><Label className="overline-label">Recursos necesarios (uno por línea)</Label>
        <Textarea rows={2} value={(form.resources_needed || []).join("\n")} onChange={(e) => setResources(e.target.value)} data-testid="bit-form-resources"
                  placeholder="ej. 40 kg emulsión&#10;Bomba sumergible&#10;Ventilador 60&quot;" /></div>

      <div className="border-t border-slate-100 pt-3">
        <div className="flex items-center justify-between mb-2">
          <p className="overline-label">Operaciones del turno ({ops.length} disponibles · {selected.size} incluidas)</p>
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={pullOps} data-testid="bit-pull-refresh">
            <ArrowsClockwise size={12} weight="bold" className="mr-1" /> Re-jalar
          </Button>
        </div>
        {ops.length === 0 ? (
          <p className="text-xs text-slate-400 italic">Sin registros de Operaciones para esta fecha + turno.</p>
        ) : (
          <div className="border border-slate-200 rounded max-h-56 overflow-y-auto divide-y divide-slate-100 text-xs">
            {ops.map((o) => {
              const on = selected.has(o.id);
              return (
                <label key={o.id} className={`flex gap-2 items-start px-2 py-1.5 cursor-pointer hover:bg-slate-50 ${on ? "bg-blue-50/40" : ""}`}
                       data-testid={`bit-op-${o.id}`}>
                  <input type="checkbox" checked={on} onChange={() => toggleOp(o.id)} className="mt-1" />
                  <div className="flex-1">
                    <p className="font-semibold text-slate-800">{EQ_LABEL[o.equipment_type] || o.equipment_type} · {o.equipment_code || "-"}</p>
                    <p className="text-[10px] text-slate-500">{o.area || "-"} · {o.operator_name || "-"} · {o.hours || 0}h · {Object.entries(o.metrics || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}</p>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </div>

      {/* Iter106: evidencia fotográfica del turno */}
      <div className="border-t border-slate-100 pt-3">
        <div className="flex items-center justify-between mb-2">
          <p className="overline-label flex items-center gap-1">
            <Camera size={13} weight="duotone" /> Fotos del turno ({(form.photos || []).length})
          </p>
        </div>
        <p className="text-[10px] text-slate-400 mb-2 italic">
          Adjunta evidencia visual (frente, incidente, avance, equipo). Máx 8 fotos, ~150 KB c/u.
          Se guardan comprimidas en la bitácora y aparecen en el PDF exportado.
        </p>
        <PhotoUploader
          value={form.photos || []}
          onChange={(next) => setForm({ ...form, photos: next })}
          max={8}
          testidPrefix="bit-photo"
        />
      </div>

      <div className="border-t border-slate-100 pt-3">
        <div className="flex items-center justify-between mb-2">
          <p className="overline-label">Materiales consumidos ({materials.length})</p>
          <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={addMat} data-testid="bit-mat-add">
            <Plus size={12} weight="bold" className="mr-1" /> Añadir material
          </Button>
        </div>
        <p className="text-[10px] text-slate-400 mb-2 italic">Al cerrar la bitácora se generan salidas de inventario referenciadas a este turno (BIT-xxxxxxxx). Vincula el material a una operación específica para trazabilidad (opcional).</p>
        {materials.length === 0 && <p className="text-xs text-slate-400 italic">Sin materiales declarados.</p>}
        <div className="space-y-2">
          {materials.map((m, idx) => {
            const it = inventoryItems.find((x) => x.id === m.item_id);
            const low = it && parseFloat(m.qty || 0) > (it.current_stock || 0);
            // Only ops selected in the current form are pickable
            const selectedOps = ops.filter((o) => selected.has(o.id));
            const linkedOp = selectedOps.find((o) => o.id === m.operation_id);
            return (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-slate-50 rounded p-2" data-testid={`bit-mat-row-${idx}`}>
                <div className="col-span-5">
                  <Select value={m.item_id || "__none__"} onValueChange={(v) => updMat(idx, "item_id", v === "__none__" ? "" : v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`bit-mat-item-${idx}`}><SelectValue placeholder="Elegir ítem…" /></SelectTrigger>
                    <SelectContent className="bg-white max-h-72 overflow-y-auto">
                      <SelectItem value="__none__">— Seleccionar —</SelectItem>
                      {inventoryItems.map((i) => (
                        <SelectItem key={i.id} value={i.id}>{i.code} · {i.description} ({i.current_stock} {i.unit})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Input type="number" step="0.01" placeholder="Cant." className="h-8 text-xs" value={m.qty} onChange={(e) => updMat(idx, "qty", e.target.value)} data-testid={`bit-mat-qty-${idx}`} />
                </div>
                <div className="col-span-3">
                  <Select value={m.operation_id || "__none__"} onValueChange={(v) => updMat(idx, "operation_id", v === "__none__" ? "" : v)}>
                    <SelectTrigger className={`h-8 text-xs ${linkedOp ? "border-blue-400" : ""}`} data-testid={`bit-mat-op-${idx}`}>
                      <SelectValue placeholder={selectedOps.length === 0 ? "Sin operaciones vinculadas" : "Vincular a operación…"} />
                    </SelectTrigger>
                    <SelectContent className="bg-white max-h-72 overflow-y-auto">
                      <SelectItem value="__none__">— Ninguna (sólo turno) —</SelectItem>
                      {selectedOps.length === 0 && (
                        <div className="px-3 py-2 text-[10px] text-slate-400 italic">Marca operaciones arriba primero.</div>
                      )}
                      {selectedOps.map((o) => (
                        <SelectItem key={o.id} value={o.id}>
                          {(EQ_LABEL[o.equipment_type] || o.equipment_type)} · {o.equipment_code || o.area || "-"} · {o.operator_name || "-"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-1">
                  <Input placeholder="Nota" className="h-8 text-xs" value={m.notes || ""} onChange={(e) => updMat(idx, "notes", e.target.value)} />
                </div>
                <div className="col-span-1 flex justify-center">
                  <button type="button" onClick={() => delMat(idx)} className="text-slate-400 hover:text-red-600" data-testid={`bit-mat-del-${idx}`}><Trash size={14} /></button>
                </div>
                {linkedOp && (
                  <p className="col-span-12 text-[10px] text-blue-700 font-semibold">
                    🔗 Vinculado a {EQ_LABEL[linkedOp.equipment_type] || linkedOp.equipment_type} — el kardex registrará <span className="font-mono">BIT-{(initial.id || "").slice(0, 8) || "xxxxxxxx"}·OP-{linkedOp.id.slice(0, 8)}</span>
                  </p>
                )}
                {low && <p className="col-span-12 text-[10px] text-red-600 font-bold">⚠ La cantidad excede el stock disponible ({it.current_stock} {it.unit}). El cierre podría fallar.</p>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Iter112: firmas de conformidad — se capturan desde la tablet sin salir del formulario */}
      <div className="border-t border-slate-100 pt-3" data-testid="bit-signatures-block">
        <div className="flex items-center justify-between mb-2">
          <p className="overline-label flex items-center gap-1">
            <SealCheck size={13} weight="duotone" className="text-emerald-600" />
            Firmas de conformidad
          </p>
          <span className="text-[10px] text-slate-500 italic">
            {[form.signature_conformity, form.signature_responsible, form.signature_silverco].filter(Boolean).length} / 3 firmadas
          </span>
        </div>
        <p className="text-[10px] text-slate-400 mb-3 italic">
          Cada firma queda con nombre visible y hash SHA-256 sobre la bitácora
          para auditoría posterior. El PDF individual y el consolidado incluyen
          las 3 firmas.
        </p>
        <div className="grid gap-3 md:grid-cols-3">
          {[
            { key: "conformity", label: "Supervisor · Conformidad",
              nameKey: "signature_conformity_name", dataKey: "signature_conformity",
              fallbackName: form.supervisor_name },
            { key: "responsible", label: "Responsable Mafrissa · Aprobación",
              nameKey: "signature_responsible_name", dataKey: "signature_responsible",
              fallbackName: "" },
            { key: "silverco", label: "SilverCO · Cliente · V°B°",
              nameKey: "signature_silverco_name", dataKey: "signature_silverco",
              fallbackName: "" },
          ].map((sig) => (
            <div key={sig.key} className="rounded-lg border border-slate-200 bg-slate-50 p-2" data-testid={`bit-sig-${sig.key}`}>
              <p className="text-[10px] uppercase font-bold text-slate-600 mb-2">{sig.label}</p>
              <SignaturePad
                value={form[sig.dataKey]}
                onChange={(dataUrl) => setForm({ ...form, [sig.dataKey]: dataUrl })}
                width={280}
                height={90}
                testid={`bit-sig-pad-${sig.key}`}
              />
              <Input
                className="h-8 text-xs mt-2"
                placeholder="Nombre de quien firma"
                value={form[sig.nameKey] || sig.fallbackName || ""}
                onChange={(e) => setForm({ ...form, [sig.nameKey]: e.target.value })}
                data-testid={`bit-sig-name-${sig.key}`}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <Button variant="outline" onClick={onCancel} className="flex-1">Cancelar</Button>
        <Button onClick={() => save(false)} disabled={busy || !form.date} className="flex-1 bg-slate-600 hover:bg-slate-700" data-testid="bit-form-save-open">
          Guardar (abierta)
        </Button>
        <Button onClick={() => save(true)} disabled={busy || !form.date} className="flex-1 bg-blue-900 hover:bg-blue-800" data-testid="bit-form-save-closed">
          Cerrar bitácora
        </Button>
      </div>
    </div>
  );
}

function EmailDialog({ log, onClose }) {
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("Adjuntamos la bitácora del turno para su registro.");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const recipients = to.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (recipients.length === 0) { toast.error("Ingrese al menos un correo destinatario"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/bitacora/logs/${log.id}/email`, { to: recipients, message });
      toast.success(`Bitácora enviada a ${r.data.to.length} destinatario(s)`);
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Enviar bitácora por correo</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">Bitácora {log.date} · {SHIFT_LABEL[log.shift]}</p>
          <div><Label className="overline-label">Destinatarios (uno por coma, punto y coma o espacio)</Label>
            <Textarea rows={2} value={to} onChange={(e) => setTo(e.target.value)} placeholder="jefe.mina@empresa.mx, planificacion@empresa.mx" data-testid="bit-email-to" /></div>
          <div><Label className="overline-label">Mensaje</Label>
            <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="bit-email-msg" /></div>
          <Button onClick={send} disabled={busy || !to} className="bg-blue-900 hover:bg-blue-800 w-full gap-2" data-testid="bit-email-send">
            <PaperPlaneTilt size={14} weight="bold" /> {busy ? "Enviando…" : "Enviar con PDF adjunto"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Bitacora() {
  const { user } = useAuth();
  const { has } = usePermissions();
  const canWrite = has("bitacora.write");
  const canEmail = has("bitacora.email");
  const [logs, setLogs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [emailingLog, setEmailingLog] = useState(null);
  const [dateFilter, setDateFilter] = useState("");
  // Iter109: rango para descargar todas las bitácoras en un PDF consolidado.
  const [periodFrom, setPeriodFrom] = useState(() => {
    const d = new Date(); d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [periodTo, setPeriodTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [periodShift, setPeriodShift] = useState("all");
  const [downloadingPeriod, setDownloadingPeriod] = useState(false);

  const load = useCallback(() => {
    const q = dateFilter ? `?date=${dateFilter}` : "";
    api.get(`/bitacora/logs${q}`).then((r) => setLogs(r.data)).catch(() => setLogs([]));
  }, [dateFilter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/hr/supervisors-lite").then((r) => setEmployees(r.data)).catch(() => setEmployees([]));
  }, []);

  const initial = useMemo(() => {
    if (!editing) {
      const base = emptyForm();
      // Pre-fill supervisor with logged-in user if we find them in employees
      const match = employees.find((e) => e.email && user?.email && e.email.toLowerCase() === user.email.toLowerCase());
      if (match) { base.supervisor_id = match.id; base.supervisor_name = match.name; }
      else base.supervisor_name = user?.name || "";
      return base;
    }
    return { ...emptyForm(), ...editing };
  }, [editing, employees, user]);

  const confirmDelete = useConfirmDelete();

  const remove = async (id, log = null) => {
    const ok = await confirmDelete({
      title: "¿Eliminar bitácora?",
      itemLabel: log ? `${log.date} · Turno ${log.shift} · ${log.supervisor || ""}` : "",
      description: "Este registro dejará huella en el historial de auditoría con tu nombre, la fecha y el motivo. No se puede deshacer.",
    });
    if (!ok) return;
    try { await apiDelete(`/bitacora/logs/${id}`, ok.reason); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const downloadPdf = async (log) => {
    try {
      await downloadFile(`/bitacora/logs/${log.id}/pdf`, `bitacora-${log.date}-${log.shift}.pdf`);
    } catch (e) { toast.error(apiError(e)); }
  };

  // Iter109: PDF consolidado del período (portada Mafrissa + resumen + cada bitácora).
  const downloadPeriodPdf = async () => {
    if (!periodFrom || !periodTo) { toast.error("Elige rango de fechas"); return; }
    if (periodFrom > periodTo) { toast.error("La fecha 'desde' debe ser anterior a 'hasta'"); return; }
    setDownloadingPeriod(true);
    try {
      const shiftQ = periodShift !== "all" ? `&shift=${periodShift}` : "";
      await downloadFile(
        `/bitacora/logs/period-pdf?date_from=${periodFrom}&date_to=${periodTo}${shiftQ}`,
        `bitacoras-${periodFrom}-a-${periodTo}.pdf`,
      );
      toast.success("PDF del período descargado");
    } catch (e) { toast.error(await apiErrorAsync(e)); }
    finally { setDownloadingPeriod(false); }
  };

  return (
    <div className="space-y-6">
      <SectionHeader icon={Notebook} title="Bitácora de turno" eyebrow="Registro por supervisor · Turno a turno · Auto-jala Operaciones" size="lg" testid="bit-header"
        right={
          <>
            <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="h-9 w-40" data-testid="bit-filter-date" />
            {canWrite && (
              <Button className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5" onClick={() => { setEditing(null); setOpen(true); }} data-testid="bit-new-log">
                <Plus size={14} weight="bold" /> Nueva bitácora
              </Button>
            )}
          </>
        } />

      {/* Iter109: descarga consolidada de bitácoras del período con portada Mafrissa */}
      <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="bit-period-panel">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="font-heading font-bold text-slate-900 flex items-center gap-2">
              <FilePdf size={18} weight="bold" className="text-blue-900" />
              PDF consolidado del período
            </p>
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
              <SealCheck size={12} weight="fill" className="text-emerald-600" />
              Portada Mafrissa UG · resumen ejecutivo · todas las bitácoras con token OID auditable
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="overline-label">Desde</Label>
              <Input type="date" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} className="h-9 w-40" data-testid="bit-period-from" />
            </div>
            <div>
              <Label className="overline-label">Hasta</Label>
              <Input type="date" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} className="h-9 w-40" data-testid="bit-period-to" />
            </div>
            <div>
              <Label className="overline-label">Turno</Label>
              <Select value={periodShift} onValueChange={setPeriodShift}>
                <SelectTrigger className="h-9 w-32" data-testid="bit-period-shift"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="dia">Día</SelectItem>
                  <SelectItem value="noche">Noche</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={downloadPeriodPdf}
              disabled={downloadingPeriod}
              className="bg-blue-900 hover:bg-blue-800 text-white gap-1.5 h-9"
              data-testid="bit-period-download"
            >
              <Files size={14} weight="bold" />
              {downloadingPeriod ? "Generando…" : "Descargar período"}
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {logs.length === 0 && <p className="text-sm text-slate-400 text-center py-8 bg-white border border-slate-200 rounded-lg">Sin bitácoras registradas.</p>}
        {logs.map((l) => (
          <div key={l.id} data-testid={`bit-card-${l.id}`} className="bg-white border border-slate-200 rounded-lg p-4 space-y-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-heading font-bold text-slate-900">
                  {l.date} · Turno <span className={`inline-block text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ml-1 align-middle ${l.shift === "dia" ? "bg-amber-100 text-amber-800" : "bg-indigo-100 text-indigo-800"}`}>{SHIFT_LABEL[l.shift]}</span>
                </p>
                <p className="text-xs text-slate-500 flex items-center gap-1.5 flex-wrap"><ProjectBadge id={l.project_id} />{l.area || "Sin zona"} · Supervisor: {l.supervisor_name || "-"}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${l.status === "closed" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}>{l.status}</span>
                <Button variant="outline" size="sm" onClick={() => downloadPdf(l)} data-testid={`bit-pdf-${l.id}`} className="h-8 gap-1">
                  <FilePdf size={13} weight="bold" /> PDF
                </Button>
                {canEmail && (
                  <Button variant="outline" size="sm" onClick={() => setEmailingLog(l)} data-testid={`bit-email-${l.id}`} className="h-8 gap-1">
                    <PaperPlaneTilt size={13} weight="bold" /> Correo
                  </Button>
                )}
                {canWrite && (
                  <>
                    <button onClick={() => { setEditing(l); setOpen(true); }} className="text-slate-400 hover:text-blue-900"><PencilSimple size={14} /></button>
                    <button onClick={() => remove(l.id, l)} className="text-slate-400 hover:text-red-600" data-testid={`bit-delete-${l.id}`}><Trash size={14} /></button>
                  </>
                )}
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-3 text-xs">
              <div><p className="overline-label mb-1">Realizado</p><p className="text-slate-700 whitespace-pre-line">{l.activities_done || "-"}</p></div>
              <div><p className="overline-label mb-1">Plan siguiente</p><p className="text-slate-700 whitespace-pre-line">{l.plan_next || "-"}</p></div>
              <div><p className="overline-label mb-1">Recursos ({(l.resources_needed || []).length})</p>
                <ul className="text-slate-700">{(l.resources_needed || []).map((r, i) => <li key={i}>• {r}</li>)}</ul>
              </div>
            </div>
            <KpiStrip logId={l.id} testid={`bit-kpis-${l.id}`} />
            <p className="text-[10px] text-slate-400">
              Operaciones vinculadas: {(l.linked_operations || []).length}
              {(l.photos_count || 0) > 0 && (
                <span className="ml-3 inline-flex items-center gap-1 text-blue-700 font-semibold">
                  <Camera size={11} weight="fill" /> {l.photos_count} foto{l.photos_count === 1 ? "" : "s"}
                </span>
              )}
            </p>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
        <DialogContent className="bg-white max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar" : "Nueva"} bitácora</DialogTitle></DialogHeader>
          <LogForm
            key={editing?.id || "new"}
            initial={initial}
            employees={employees}
            onSaved={() => { setOpen(false); setEditing(null); load(); }}
            onCancel={() => { setOpen(false); setEditing(null); }}
          />
        </DialogContent>
      </Dialog>

      {emailingLog && <EmailDialog log={emailingLog} onClose={() => setEmailingLog(null)} />}
    </div>
  );
}
